import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { DropdownSummary } from '../../../shared/dropdown/DropdownSummary';
import { LongText } from '../../../shared/longtext/LongText';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import { EntityType, fetchMyCompanyEntitiesStarted, fetchMyEntityGroupsStarted, getAllEntities, getEntityIdsForGroup, getMyCompanyEntities, GroupEntity, GroupEntityContent, updateEntityContent, updateEntityValue, updateGroupEntityIds } from '../store';
import styles from './EntityType.module.scss';

interface GroupEntityFormProps {
    entity: GroupEntity;
}

export const GroupEntityForm: React.FC<GroupEntityFormProps> = ({ entity }) => {
    const dispatch = useAppDispatch();
    useFetchStarted([fetchMyCompanyEntitiesStarted(), fetchMyEntityGroupsStarted()]);
    const handleUpdateValue = (key: string, value: string | Date | null) => dispatch(updateEntityValue(key, value));
    const handleUpdateContent = (key: keyof GroupEntityContent, value: string | string[] | null) => dispatch(updateEntityContent(key, value));
    const { name, content: { description }, myCompany, entityId } = entity;
    const allEntities = useAppSelector(getAllEntities);
    const companyEntities = useMemo(() => allEntities.length > 0 && allEntities.filter(({ type }) => type === EntityType.COMPANY) || [], [allEntities]);
    const companyEntityOptions = useMemo(() => companyEntities.length > 0 ? companyEntities.map(({ name, entityId }) => ({ value: entityId.toString(), label: name })) : [], [companyEntities]);
    const myCompanyEntities = useAppSelector(getMyCompanyEntities);
    const myCompanyList = useMemo(() => myCompanyEntities.length ? myCompanyEntities.map(({ name, entityId }) => ({ label: name, value: entityId.toString() })) : [], [myCompanyEntities]);
    const groupEntityIds = useAppSelector(getEntityIdsForGroup);

    const groupEntityOptions = useMemo(() => myCompany === 1 ? myCompanyList : companyEntityOptions, [myCompany, myCompanyList, companyEntityOptions]);

    const updateDropdownOption = (option: DropdownOption | Options<DropdownOption> | null) => {
        let selected: string[] = [];
        if (!isNull(option) && (option as Options<DropdownOption>).length > 0) {
            selected = (option as Options<DropdownOption>).map(({ value }) => value);
        }
        dispatch(updateGroupEntityIds(selected));
    };

    const toggleGroupType = useCallback(() => {
        const updatedMyCompany = myCompany === 1 ? 0 : 1;
        dispatch(updateGroupEntityIds([]));
        dispatch(updateEntityValue('myCompany', updatedMyCompany));
    }, [dispatch, myCompany]);

    const selectedCompanyEntities = useMemo(() => {
        if (myCompany === 1 && myCompanyEntities && groupEntityIds) {
            return myCompanyEntities.filter(({ entityId }) => groupEntityIds.includes(entityId.toString())).map(({ name, entityId }) => ({ label: name, value: entityId.toString() }));
        }
        if (myCompany === 0 && groupEntityIds) {
            return companyEntities.filter(({ entityId }) => groupEntityIds.includes(entityId.toString())).map(({ name, entityId }) => ({ label: name, value: entityId.toString() }));
        }
        return [];
    }, [myCompanyEntities, groupEntityIds, myCompany, companyEntities]);

    const entityDropdownLabel = useMemo(() => `${myCompany ? 'My ' : ''}Company Entities`, [myCompany]);

    const counterpartiesGroupLabel = 'This is a group of my home entities AND trading counterparties for the purpose of permissioning';
    const homeEntitiesGroupLabel = 'This is a group of my home entities for segregated analytical considerations';

    return (
        <div className={styles.groupEntityContentWrapper} data-testid='create-entity-group-content-wrapper'>
            <Text
                label='Group Name'
                testId='entity-group-name'
                onChange={e => handleUpdateValue('name', e.target.value)}
                value={name}
                maxLength={128}
                placeholder='Group Name...'
            />
            <LongText
                onChange={e => handleUpdateContent('description', e)}
                value={description}
                label='Group Description'
                placeholder='Group Description...'
                width='100%'
            />
            <div className={styles.groupPurposeWrapper}>
                <div className={styles.label}>Group Purpose:</div>
                {isNull(entityId) ?
                    <div className={styles.groupTypeOptions}>
                        <div className={styles.groupTypeWrapper}>
                            <div className={styles.groupType}>{counterpartiesGroupLabel}</div>
                            <Toggle
                                checked={myCompany === 0}
                                onChange={toggleGroupType}
                                testId='calculator-field-free-text-option'
                            />
                        </div>
                        <div className={styles.groupTypeWrapper}>
                            <div className={styles.groupType}>{homeEntitiesGroupLabel}</div>
                            <Toggle
                                checked={myCompany === 1}
                                onChange={toggleGroupType}
                                testId='calculator-field-free-text-option'
                            />
                        </div>
                    </div> : <div className={styles.groupType}>{myCompany === 1 ? homeEntitiesGroupLabel : counterpartiesGroupLabel}</div>
                }
            </div>
            <DropdownSummary
                label={entityDropdownLabel}
                testId='entity-jurisdiction'
                onChange={updateDropdownOption}
                value={selectedCompanyEntities}
                options={groupEntityOptions}
                marginBottom='10px'
                isClearable
                isMulti
            />
            <div className={styles.scrollableGroupEntities}>
                {isNull(selectedCompanyEntities) ? <div className={styles.groupEntitiesPlaceholder}>Please select entities for the group</div> : <Scrollable>
                    {selectedCompanyEntities.map((option, index) => (
                        <div key={index} className={styles.groupEntity}>{option.label}</div>
                    ))}
                </Scrollable>}
            </div>
        </div>
    );
};
